// ---------------------------------------------------------------------------
// Floor domain types
// Ported from trusstor_dashboard/shared/interfaces/
// ---------------------------------------------------------------------------

import { z } from 'zod';
import { dateField } from './_shared.js';
import { IMinimizedProfession, IProfessionWithActiveTags } from './profession.js';
import type { IManagerMergedTag } from './manager.js';
import type { IUtilityMergedTag } from './utility.js';
import type { IMergedTag } from './tag.js';

// ---------------------------------------------------------------------------
// AreaTypeEnum
// (from trusstor_dashboard/shared/interfaces/AreaType.enum.ts)
// Mixed casing (Kitchens, Toilets vs SCREAMING_SNAKE for others) matches upstream verbatim.
// ---------------------------------------------------------------------------

export enum AreaTypeEnum {
	APARTMENT = 'apartment',
	GENERAL = 'general',
	GARAGE = 'garage',
	CORE = 'core',
	LOBBY = 'lobby',
	STRUCTURE = 'structure',
	OFFICES = 'offices',
	HOTEL_ROOMS = 'hotelRooms',
	PRODUCTION_LINES = 'productionLines',
	COMMERCIALS = 'commercials',
	WET_ROOMS = 'wetRooms',
	MECHANICAL_ROOMS = 'mechanicalRooms',
	FACADE = 'facade',
	ELEVATORS = 'elevators',
	STAIRS = 'stairs',
	Kitchens = 'kitchens',
	Toilets = 'toilets',
}

// ---------------------------------------------------------------------------
// baseFloorSchema / IBaseFloor
// (from trusstor_dashboard/shared/interfaces/IBaseFloor.ts)
// ---------------------------------------------------------------------------

export const baseFloorSchema = z.object({
	floorId: z.string(),
	floorNick: z.string(),
});

export type IBaseFloor = z.infer<typeof baseFloorSchema>;

// ---------------------------------------------------------------------------
// floorSchema / IFloor
// (from trusstor_dashboard/shared/interfaces/IFloor.ts)
// ---------------------------------------------------------------------------

export const floorSchema = baseFloorSchema.extend({
	shortFloorNick: z.string(),
	projectId: z.string(),
	buildingId: z.string().optional(),
	isHoist: z.boolean().optional(),
	isSpecialFloor: z.boolean().optional(),
	isMoving: z.boolean().optional(),
	isGroundFloor: z.boolean().optional(),
});

export type IFloor = z.infer<typeof floorSchema>;

// ---------------------------------------------------------------------------
// configFloorSchema / IConfigFloor
// (from trusstor_dashboard/shared/interfaces/IConfigFloor.ts)
// ---------------------------------------------------------------------------

export const configFloorSchema = z
	.object({
		projectId: z.string(),
		floorId: z.string(),
		floorNick: z.string(),
		shortFloorNick: z.string(),
		isSpecialFloor: z.boolean().default(false),
		isMovingFloor: z.boolean().default(false),
		isHoist: z.boolean().default(false),
		isGroundFloor: z.boolean().default(false),
		buildingId: z.string().optional(),
		matchingFixedFloorId: z.string().optional(),
		matchingFixedFloorNick: z.string().optional(),
		matchingFixedShortFloorNick: z.string().optional(),
		isMatchingFloorFixed: z.boolean().optional(),
		matchingFixedFloorChangeTimestamp: dateField.optional(),
	})
	.passthrough();

export type IConfigFloor = z.infer<typeof configFloorSchema>;

// ---------------------------------------------------------------------------
// baseAreaSchema / IBaseArea (from trusstor IBaseArea.ts)
// ---------------------------------------------------------------------------

export const baseAreaSchema = z
	.object({
		areaId: z.string(),
		areaNick: z.string(),
	})
	.passthrough();

export type IBaseArea = z.infer<typeof baseAreaSchema>;

// ---------------------------------------------------------------------------
// configAreaSchema / IConfigArea
// (from trusstor_dashboard/shared/interfaces/IConfigArea.ts)
// ---------------------------------------------------------------------------

export const configAreaSchema = z
	.object({ ...configFloorSchema.shape, ...baseAreaSchema.shape })
	.extend({
		areaType: z.enum(AreaTypeEnum).optional(),
		areaSortIndex: z.number().optional(),
	})
	.passthrough();

export type IConfigArea = z.infer<typeof configAreaSchema>;

// ---------------------------------------------------------------------------
// areaViewSchema / IAreaView
// (from trusstor_dashboard/shared/interfaces/IAreaView.ts)
// ---------------------------------------------------------------------------

export const areaViewSchema = z
	.object({ ...baseAreaSchema.shape, ...configFloorSchema.shape })
	.extend({
		tagIds: z.array(z.string()),
	})
	.passthrough();

export type IAreaView = z.infer<typeof areaViewSchema>;

// ---------------------------------------------------------------------------
// floorViewSchema / IFloorView
// (from trusstor_dashboard/shared/interfaces/IFloorView.ts)
// ---------------------------------------------------------------------------

export const floorViewSchema = configFloorSchema
	.extend({
		matchingFixedFloorLastUpdateDate: dateField.optional(),
		isMovingFloorStatic: z.boolean().optional(),
		isMovingFloorStaticChangeDate: dateField.optional(),
		areas: z.array(areaViewSchema),
		tagIds: z.array(z.string()),
	})
	.passthrough();

export type IFloorView = z.infer<typeof floorViewSchema>;

// ---------------------------------------------------------------------------
// IMergedAreaView<T>
// (from trusstor_dashboard/shared/interfaces/IMergedAreaView.ts)
// ---------------------------------------------------------------------------

export interface IMergedAreaView<T extends IMinimizedProfession = IMinimizedProfession> extends IAreaView {
	managers: IManagerMergedTag[];
	activeProfessions: IProfessionWithActiveTags<T>[];
	utilities: IUtilityMergedTag<T>[];
}

// ---------------------------------------------------------------------------
// IMergedFloorView<T>
// (from trusstor_dashboard/shared/interfaces/IMergedFloorView.ts)
// ---------------------------------------------------------------------------

export interface IMergedFloorView<T extends IMinimizedProfession = IMinimizedProfession>
	extends Omit<IFloorView, 'areas'> {
	managers: IManagerMergedTag[];
	activeProfessions: IProfessionWithActiveTags<T>[];
	tags: IMergedTag<T>[];
	areas: IMergedAreaView<T>[];
	utilities?: IUtilityMergedTag<T>[];
}
